import React, { useMemo, useState } from 'react';
// @ts-ignore
import ReactMediumImageZoom from 'react-medium-image-zoom';
import c from 'classnames';

import useToggle from '@lyearn/core/hooks/useToggle';
import { waitForFn } from '@lyearn/core/utils/function';
import { Box } from '@lyearn/ui';

import { Caption, useCalculatedWidth } from '../../../components/ResizeContainer';
import { allTransition, nodeContainerWidth } from '../../../components/ResizeContainer';
import HotspotContainer from '../../../components/ResizeContainer/Hotspot';
import { ImageComponentProps } from '../types';
import { ImageLayoutTypes } from '../types';
import { imageLayoutStyles, imageStyles } from './styles';

const IMAGE_ZOOM_DEFAULT_STYLES = {
  zoomContainer: { zIndex: 1301 },
}; // required when opened in a modal, modal z-index is 1300

const ImageZoom: React.FC<{
  defaultStyles: Record<string, any>;
  image: Record<string, any>;
  zoomImage: Record<string, any>;
  zoomMargin: number;
}> = ({ defaultStyles, image, zoomImage, zoomMargin }) => {
  const [disabled, { off }] = useToggle(true);
  if (disabled) {
    return (
      <img
        {...image}
        onLoad={(e: any) => {
          // should not show zoom for scrollable images
          const shouldZoom = e.target!.height < globalThis.innerHeight;
          if (shouldZoom) {
            off();
          }
        }}
      />
    );
  }

  return (
    <ReactMediumImageZoom
      defaultStyles={defaultStyles}
      image={{
        ...image,
      }}
      zoomImage={zoomImage}
      zoomMargin={zoomMargin}
    />
  );
};

const renderTableImage = (alt?: string, src?: string, srcset?: string) => (
  <div className={c(allTransition, 'my-8 w-full')}>
    <ImageZoom
      defaultStyles={IMAGE_ZOOM_DEFAULT_STYLES}
      image={{ alt, src, srcSet: srcset, className: 'h-full object-cover' }}
      zoomImage={{ src, srcSet: srcset, className: 'object-contain' }}
      zoomMargin={40}
    />
  </div>
);

const ImageComponentReadOnly: React.FC<ImageComponentProps> = (props) => {
  const {
    src,
    srcset,
    alt,
    width,
    caption,
    height,
    showFullWidth = false,
    layout = ImageLayoutTypes.Card,
    hotspots,
    hotspotsEnabled,
  } = props;

  const calculatedWidth = useCalculatedWidth(width as number);
  const [nodeContainer, setNodeContainer] = useState<HTMLDivElement>();
  const { width: nodeWidth, height: nodeHeight } = useMemo(
    () => nodeContainer?.getBoundingClientRect() || { width: undefined, height: undefined },
    [nodeContainer],
  );

  if (!src || !calculatedWidth) {
    return null;
  }

  return (
    <>
      {showFullWidth ? (
        renderTableImage(alt, src, srcset)
      ) : (
        <div
          ref={(newNode) => {
            // we want to pass the height and width to hotspot container to
            // set the hotspots at the right position. Due to suspense lazy,
            // everything on the dom is displayed to none so the render
            // is happened already but height and width is set to zero.
            // css style(dom style is set to display block/auto after lazy
            // loading completion changes doesn't re-render the component
            // if we are using useResizeObserver or any similar approach.
            if (newNode != null) {
              waitForFn(
                () => (newNode.getBoundingClientRect().height ? newNode : null),
                () => setNodeContainer(newNode),
                { retryIn: 100, maxRetry: 10 },
              );
            }
          }}
          className={c(
            'group relative mt-32 mb-16 end-1/2 start-1/2',
            allTransition,
            imageStyles,
            imageLayoutStyles(layout),
            nodeContainerWidth(calculatedWidth),
            ImageLayoutTypes.Web === layout ? 'image-web-container' : '',
          )}
          style={{
            height: height && `${height}px`,
          }}>
          {hotspotsEnabled ? (
            <HotspotContainer
              height={nodeHeight}
              hotspots={hotspots!}
              nodeElement={nodeContainer!}
              readOnly={true}
              width={nodeWidth}
            />
          ) : null}
          {ImageLayoutTypes.Web === layout ? (
            <Box className="flex h-24 w-full p-8">
              {[0, 1, 2].map((value) => (
                <Box
                  key={value}
                  className={c('h-8 w-8 rounded-8 bg-interactive-secondary', value ? 'ms-4' : '')}
                />
              ))}
            </Box>
          ) : null}
          <ImageZoom
            defaultStyles={IMAGE_ZOOM_DEFAULT_STYLES}
            image={{ alt, src, srcSet: srcset, className: 'h-full object-cover' }}
            zoomImage={{ src, srcSet: srcset }}
            zoomMargin={40}
          />
        </div>
      )}
      <Caption readOnly caption={caption} />
    </>
  );
};

export default ImageComponentReadOnly;
